import { BaseAnnotationTool } from "./base/BaseAnnotationTool.js";

// State
import { getToolState } from "component/toolManager/toolState.js";
import toolColors from "component/toolManager/toolColors.js";

// Drawing
import { getNewContext, draw, setShadow, drawLine, drawCircle } from "../../draw/index.js";
import drawHandles from "../../draw/drawHandles.js";
import math from "core/math";
import { lengthCursor } from "../cursors/index.js";

import BaseBrushTool from "component/molecules/viewport/tools/annotation/base/BaseBrushTool";
import CustomEvent from "core/customEvent";

import { getEnabledElements } from "component/toolManager/enabledElements.js";


/**
 * @public
 * @class LineTool
 * @memberof Tools.Annotation
 * @classdesc Tool for drawing line
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class LineTool extends BaseAnnotationTool {
  constructor(configuration = {}) {
    const defaultConfig = {
      name: "Pencil",
      supportedInteractionTypes: ["Mouse", "Touch"],
      svgCursor: lengthCursor,
    };
    const initialConfiguration = Object.assign(defaultConfig, configuration);

    super(initialConfiguration);

    this.initialConfiguration = initialConfiguration;

    // 휠 이벤트
    this.brushRadius = 10;
    this.isMouseDown = false;

    // 커서 이벤트
    this.updateOnMouseMove = true;

    //
    this._lastImageCoords = { x: 256, y: 256 };
    this.modifiedPixelIndices = new Set();
  }

  mouseMoveCallback(evt) {
    const { currentPoints } = evt.detail;
    this._lastImageCoords = currentPoints.image;
  
    const element = evt.detail.element;
    if (element) {
      const enabledElement = getEnabledElements().find(
        el => el.element === element
      );
      if (enabledElement) {
        enabledElement.needsRedraw = true;
      }
    }
  }

  createNewMeasurement(eventData) {
    const goodEventData =
      eventData && eventData.currentPoints && eventData.currentPoints.image;

    if (!goodEventData) {
      return;
    }

    const { x, y } = eventData.currentPoints.image;

    return {
      visible: true,
      active: true,
      color: undefined,
      storeMiddlePoint: true,
      middlePoint: [],
      handles: {
        start: {
          x,
          y,
          highlight: true,
          active: false
        },
        end: {
          x,
          y,
          highlight: true,
          active: true
        }
      },
      willbeRemove: true,
      moveCount: 0
    };
  }

  /**
   * @description coord 점이 그려진 도구 데이터와 특정 거리 이내에 있는지 없는지 판별
   *
   * @param {HTMLElement} element   canvas element
   * @param {Array<Point>} data  source point array
   * @param {Point} coords  target point
   * @returns {Boolean}
   */
  pointNearTool(element, data, coords) {
    const hasStartAndEndHandles =
      data && data.handles && data.handles.start && data.handles.end;
    const validParameters = hasStartAndEndHandles;

    if (!validParameters) {
      return false;
    }

    if (data.visible === false) {
      return false;
    }



    /// 중간점을 새로운 배열에 복사한다.
    let linePoints = JSON.parse(JSON.stringify(data.middlePoint)); //참조없는 복사

    linePoints.forEach((value, index) => {
      linePoints[index] = math.coordinate.pixelToCanvas(element, value);
    });

    /// 점 그룹과 특정점이 가까이 있는지 체크
    return math.point.getMinDistance(linePoints, coords) < 25;
  }

  _handleWheelEvent(event) {
    // if (!this.isMouseDown) return;
    if (!event.shiftKey) return; // Shift 키를 누르지 않으면 동작 안 함
    event.preventDefault();
  
    if (event.deltaY < 0) {
      this.brushRadius = Math.min(this.brushRadius + 1, 15);
    } else {
      this.brushRadius = Math.max(this.brushRadius - 1, 1);
    }
    
   // 🔄 즉시 렌더링 되도록 needsRedraw 플래그 설정
    const totalEnabledElements = getEnabledElements();
    if (totalEnabledElements && totalEnabledElements.length > 0) {
      for (const enabledElement of totalEnabledElements) {
        enabledElement.needsRedraw = true;

        // 이벤트 수동 트리거
        CustomEvent.triggerEvent(
          enabledElement.element,
          CustomEvent.EVENTS.IMAGE_RENDERED,
          enabledElement
        );
      }
    }
  }

  // Mouse up 동작할 때 기존 데이터 추가
  removeToolData(evt) {
    if (!evt || !evt.element || !evt.element.element) return;
    const toolState_lineTool = getToolState(evt.element.element, this.name);
    if (!toolState_lineTool || !toolState_lineTool.data) return;
  
    // toolData 전체 초기화
    toolState_lineTool.data.length = 0;
    let totalEnabledElement = getEnabledElements();
    let mainEnabledElement = totalEnabledElement[0];
    let toolState = getToolState(
      mainEnabledElement.element,
      BaseBrushTool.getReferencedToolDataName()
    );

    const toolData = toolState.data;
    let pixelData =  toolData[0].pixelData;
    let originalPixelData =  toolData[0].originalPixelData
    if(toolData[0].colorCount !== 1)
      {
        return
      }

    // undo stack의 크기가 10개 이상이면 없어진다.
    if (toolData[0].undoStack.length >= 10) {
      toolData[0].undoStack.shift(); // 가장 오래된 항목 제거
    }
    const undopixelData = pixelData.slice();
    toolData[0].undoStack.push(undopixelData);

    if (!toolData || !toolData[0] || !toolData[0].originalPixelData) 
    {
        return;
    }
    
    const image = mainEnabledElement.image;
    const width = image.columns;
    const height = image.rows;
    // const brushRadius = Math.floor(this.brushRadius * 0.8);
    if (this.modifiedPixelIndices && this.modifiedPixelIndices.size > 0) {
      for (const index of this.modifiedPixelIndices) {
        const py = Math.floor(index / width);
        const px = index % width;
        const d_index = py * width + px;
        pixelData[d_index] = toolData[0].color;
        originalPixelData[d_index] = toolData[0].color;
      }
      this.modifiedPixelIndices.clear(); // 초기화
    }

    toolData[0].invalidated = true; // ✅ invalidate 설정

    CustomEvent.triggerEvent(
      mainEnabledElement.element,
      CustomEvent.EVENTS.IMAGE_RENDERED,
      mainEnabledElement
    );
    toolData[0].invalidated = false;
  }

  deactivate() {
    if (typeof this.removeToolData === "function" && this.element) {
      this.removeToolData({ element: this.element });
    }
    // 기타 deactivate 로직이 있다면 여기에 추가
  }

  /**
   * @description 도구 정보를 이용해 도구 렌더링
   * @method renderToolData
   *
   * @param {EVENT} evt
   * @returns
   * @memberof LengthTool
   */
  renderToolData(evt) {
    // 휠 이벤트
    if (!this._wheelHandlerRegistered) {
      let totalEnabledElement = getEnabledElements();
      let mainEnabledElement = totalEnabledElement[0];
      
      const viewport = mainEnabledElement.viewport;
      // const scale = viewport?.scale ?? 1;
      let scale = 1;

      if (viewport && typeof viewport.scale !== "undefined") {
        scale = viewport.scale;
      }
      let element = mainEnabledElement.element;
      if (element) {
        // element.addEventListener("mousedown", () => {
        //   this.isMouseDown = true;
        // });
    
        // element.addEventListener("mouseup", () => {
        //   this.isMouseDown = false;
        // });
    
        element.addEventListener("wheel", this._handleWheelEvent.bind(this), { passive: false });
    
        this._wheelHandlerRegistered = true;
      }
    }
    // END 휠 이벤트

    const eventData = evt.detail;
    const context = getNewContext(eventData.canvas);
    const { element } = eventData;
    const brushRadius = this.brushRadius || 10;
    const coords = this._lastImageCoords;
    const color = "#00ff00";

    const toolData = getToolState(evt.currentTarget, this.name);
    let totalEnabledElement = getEnabledElements();
    let mainEnabledElement = totalEnabledElement[0];
    let scale = 1;
    if (mainEnabledElement && mainEnabledElement.viewport &&
        typeof mainEnabledElement.viewport.scale !== "undefined") {
      scale = mainEnabledElement.viewport.scale;
    }
    const imageRadius = this.brushRadius || 10; // ✅ 이미지 픽셀 기준
    const canvasRadius = imageRadius * scale;   // ✅ 화면에 그릴 반지름
    const pixelRadius = Math.floor(imageRadius); // ✅ 마스크에 적용할 반지름
    
    let toolState = getToolState(
      mainEnabledElement.element,
      BaseBrushTool.getReferencedToolDataName()
    );
    
    if (!toolState || !toolState.data || !toolState.data[0]) 
    {
      return; 
    }

    const effectiveRadius = Math.floor(brushRadius * 1)

    if (coords && toolState.data[0].mode === true) {
      const canvasCoords = math.coordinate.pixelToCanvas(element, coords);
      draw(context, ctx => {
        ctx.beginPath();
        // ctx.arc(canvasCoords.x, canvasCoords.y, effectiveRadius, 0, 2 * Math.PI);
        ctx.arc(canvasCoords.x, canvasCoords.y, canvasRadius, 0, 2 * Math.PI);
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = 1;
        ctx.fill();
        ctx.stroke();
      });
    }
    else return;

    if (!toolData) {
      return;
    }
    
    let pixelData = toolState.data[0].pixelData;
    let originalPixelData = toolState.data[0].originalPixelData;
    const image = mainEnabledElement.image;
    const width = image.columns;
    const height = image.rows;


    // const pixelRadius = Math.floor(effectiveRadius * 0.5);
    // 색상 한개만 선택했을 때 동작
    if(toolState.data[0].colorCount !== 1)
      {
        return
      }

    for (let i = 0; i < toolData.data.length; i++) {
      const data = toolData.data[i];
      if (data.visible === false || data.willbeRemove === true) {
        continue;
      }
  
      const linePoints = JSON.parse(JSON.stringify(data.middlePoint));
      if (!linePoints || linePoints.length === 0) {
        continue;
      }
  
      if (linePoints.length === 1) {
        const duplicated = JSON.parse(JSON.stringify(linePoints[0]));
        duplicated.x += 0.001; // 아주 작게 이동시켜 선처럼 처리
        linePoints.push(duplicated);
      }
      
      draw(context, ctx => {
        setShadow(ctx, this.configuration);
        for (let j = 0; j < linePoints.length - 1; j++) {
          const startPixel = math.coordinate.pixelToCanvas(element, linePoints[j]);
          const endPixel = math.coordinate.pixelToCanvas(element, linePoints[j + 1]);
        
          const distance = Math.hypot(endPixel.x - startPixel.x, endPixel.y - startPixel.y);
          // const step = effectiveRadius * 2;
          const step = canvasRadius * 2;
          const steps = Math.max(1, Math.ceil(distance / step));
          for (let k = 0; k <= steps; k++) {
            const t = k / steps;
            const x = startPixel.x + (endPixel.x - startPixel.x) * t;
            const y = startPixel.y + (endPixel.y - startPixel.y) * t;
        
            const pixel = math.coordinate.canvasToPixel(element, { x, y });
            const px = Math.floor(pixel.x);
            const py = Math.floor(pixel.y);
            if (px >= 0 && px < width && py >= 0 && py < height) {
              for (let dy = -pixelRadius; dy <= pixelRadius; dy++) {
                for (let dx = -pixelRadius; dx <= pixelRadius; dx++) {
                  const distSq = dx * dx + dy * dy;
                  if (distSq <= pixelRadius * pixelRadius) {
                    const nx = px + dx;
                    const ny = py + dy;
              
                    if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
                      const d_index = ny * width + nx;
                      if (pixelData[d_index] !== toolState.data[0].color)
                      {
                        // 저장된 값을 removeToolData()함수에 적용하여 수정한다.
                        this.modifiedPixelIndices.add(d_index);
                        // console.log("index: ", index);
                      }
                    }
                  }
                }
              }
            ctx.beginPath();
            // ctx.arc(x, y, effectiveRadius, 0, Math.PI * 2);
            ctx.arc(x, y, canvasRadius, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();
            
          }
        }
      }
      });
    }
  } 
}
